import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChevronDownIcon, ChevronRightIcon, DocumentIcon, DocumentTextIcon, EnvelopeIcon, FilmIcon, FolderIcon,
  FolderOpenIcon, FunnelIcon, HomeIcon, MusicalNoteIcon, PhotoIcon, PresentationChartBarIcon, TableCellsIcon,
} from '@heroicons/react/24/outline';
import { baumLaden, type Baum, type BaumDatei, type BaumOrdner } from '../api/client';
import DateiVorschau from '../components/datei/DateiVorschau';
import { ART_ICON } from '../components/laufwerk/QuellenKarte';
import { bytes, datumCH, zahl } from '../utils/laufwerk';

/**
 * Dateien (A24): das Laufwerk als Ordnerbaum wie im Explorer — links der Baum, rechts der Inhalt des gewählten
 * Ordners, ein Klick auf eine Datei öffnet sie in der Vorschau (Original vom Laufwerk bzw. der Quelle, Text,
 * Speicherort). Jeder sieht nur, was er sehen darf (das Backend filtert). Adresse: `?pfad=Ordner/Unterordner/` und
 * `?datei=<id>` — so funktionieren Zurück-Taste und Links.
 */

const ENDUNG_ICON: [RegExp, typeof DocumentIcon][] = [
  [/^(xlsx?|csv|ods|numbers)$/, TableCellsIcon],
  [/^(pptx?|odp|key)$/, PresentationChartBarIcon],
  [/^(png|jpe?g|gif|heic|tiff?|bmp|webp|svg)$/, PhotoIcon],
  [/^(eml|msg)$/, EnvelopeIcon],
  [/^(mp4|mov|m4v|avi|mkv|wmv)$/, FilmIcon],
  [/^(mp3|m4a|wav|aiff?|flac|ogg)$/, MusicalNoteIcon],
  [/^(pdf|docx?|odt|rtf|pages|txt|md)$/, DocumentTextIcon],
];

function dateiIcon(endung: string) {
  return ENDUNG_ICON.find(([re]) => re.test(endung.toLowerCase()))?.[1] ?? DocumentIcon;
}

const STATUS: Record<string, [string, string]> = {
  indexiert: ['durchsuchbar', 'badge-ok'],
  hochladen: ['wird gelesen', 'badge-warn'], wartet: ['wird gelesen', 'badge-warn'], in_arbeit: ['wird gelesen', 'badge-warn'],
  fehler: ['nicht lesbar', 'badge-err'], zu_gross: ['zu gross', 'badge-neutral'], nicht_unterstuetzt: ['nur abgelegt', 'badge-neutral'],
};

function OrdnerIcon({ o, offen }: { o: BaumOrdner; offen?: boolean }) {
  const Icon = o.quelle ? (ART_ICON[o.quelle.art] ?? FolderIcon) : offen ? FolderOpenIcon : FolderIcon;
  return <Icon className={`h-4 w-4 shrink-0 ${o.quelle ? 'text-primary-500' : 'text-amber-500'}`} />;
}

type Knoten = { laedt: boolean; ordner?: BaumOrdner[]; fehler?: string };

/** Linker Baum: lädt jede Ebene erst beim Aufklappen. */
function BaumLinks({ pfad, waehlen }: { pfad: string; waehlen: (p: string) => void }) {
  const [knoten, setKnoten] = useState<Record<string, Knoten>>({});
  const [offen, setOffen] = useState<Record<string, boolean>>({ '': true });

  const laden = useCallback((p: string) => {
    setKnoten((k) => (k[p] ? k : { ...k, [p]: { laedt: true } }));
    baumLaden(p)
      .then((b) => setKnoten((k) => ({ ...k, [p]: { laedt: false, ordner: b.ordner } })))
      .catch((e: any) => setKnoten((k) => ({ ...k, [p]: { laedt: false, fehler: e?.message ?? String(e) } })));
  }, []);

  // Gewählter Ordner: alle Ebenen darüber aufklappen und laden
  useEffect(() => {
    const teile = pfad.split('/').filter(Boolean);
    const ebenen = [''].concat(teile.map((_t, i) => teile.slice(0, i + 1).join('/') + '/'));
    setOffen((o) => ({ ...o, ...Object.fromEntries(ebenen.map((e) => [e, true])) }));
    ebenen.forEach((e) => { if (!knoten[e]) laden(e); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfad, laden]);

  const umschalten = (p: string) => {
    setOffen((o) => ({ ...o, [p]: !o[p] }));
    if (!knoten[p]) laden(p);
  };

  const ebene = (p: string, tiefe: number): React.ReactNode => {
    const k = knoten[p];
    if (!k || k.laedt) return <div className="py-1 text-xs text-ps-dark-400" style={{ paddingLeft: 20 + tiefe * 14 }}>Lade …</div>;
    if (k.fehler) return <div className="py-1 text-xs text-rose-700" style={{ paddingLeft: 20 + tiefe * 14 }}>{k.fehler}</div>;
    return (k.ordner ?? []).map((o) => {
      const auf = !!offen[o.pfad];
      const aktiv = o.pfad === pfad;
      return (
        <div key={o.pfad}>
          <div className={`group flex items-center rounded-md pr-1 ${aktiv ? 'bg-primary-50 text-primary-800' : 'text-ps-dark-700 hover:bg-gray-100'}`}
            style={{ paddingLeft: tiefe * 14 }}>
            <button type="button" onClick={() => umschalten(o.pfad)} aria-label={auf ? `${o.name} zuklappen` : `${o.name} aufklappen`} aria-expanded={auf}
              className="flex h-7 w-5 shrink-0 items-center justify-center text-ps-dark-400 hover:text-ps-dark-700">
              {auf ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
            </button>
            <button type="button" onClick={() => { waehlen(o.pfad); if (!auf) umschalten(o.pfad); }}
              className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left text-[13px]" title={o.pfad}>
              <OrdnerIcon o={o} offen={auf} /><span className="truncate">{o.name}</span>
            </button>
          </div>
          {auf && ebene(o.pfad, tiefe + 1)}
        </div>
      );
    });
  };

  return (
    <nav aria-label="Ordnerbaum" className="min-h-0 flex-1 overflow-auto p-2">
      <button type="button" onClick={() => waehlen('')}
        className={`flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[13px] font-medium ${pfad === '' ? 'bg-primary-50 text-primary-800' : 'text-ps-dark-800 hover:bg-gray-100'}`}>
        <HomeIcon className="h-4 w-4 text-ps-dark-400" /> Laufwerk
      </button>
      {ebene('', 0)}
    </nav>
  );
}

export default function Dateien() {
  const [sp, setSp] = useSearchParams();
  const pfad = sp.get('pfad') ?? '';
  const datei = sp.get('datei');
  const [baum, setBaum] = useState<Baum | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let weg = false;
    setBaum(null); setFehler(null); setFilter('');
    baumLaden(pfad)
      .then((b) => { if (!weg) setBaum(b); })
      .catch((e: any) => { if (!weg) setFehler(e?.message ?? String(e)); });
    return () => { weg = true; };
  }, [pfad]);

  const waehlen = useCallback((p: string) => setSp(p ? { pfad: p } : {}), [setSp]);
  const oeffnen = (d: BaumDatei) => setSp({ ...(pfad ? { pfad } : {}), datei: d.id });
  const schliessen = () => setSp(pfad ? { pfad } : {}, { replace: true });

  const f = filter.trim().toLowerCase();
  const ordner = useMemo(() => (baum?.ordner ?? []).filter((o) => !f || o.name.toLowerCase().includes(f)), [baum, f]);
  const dateien = useMemo(() => (baum?.dateien ?? []).filter((d) => !f || d.name.toLowerCase().includes(f)), [baum, f]);
  const leer = baum && baum.ordner.length === 0 && baum.dateien.length === 0;

  return (
    <div className="mx-auto flex h-[calc(100dvh-7rem)] max-w-[1800px] gap-4">
      <aside className={`hidden w-72 shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ${datei ? '2xl:flex' : 'lg:flex'}`}>
        <div className="border-b border-gray-100 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-ps-dark-400">Ordner</div>
        <BaumLinks pfad={pfad} waehlen={waehlen} />
      </aside>

      <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="space-y-2 border-b border-gray-100 px-4 py-3">
          <nav aria-label="Pfad" className="flex min-w-0 flex-wrap items-center gap-1 text-sm">
            <button onClick={() => waehlen('')} className="flex items-center gap-1 rounded px-1 py-0.5 font-medium text-ps-dark-700 hover:bg-gray-100">
              <HomeIcon className="h-4 w-4 text-ps-dark-400" /> Laufwerk
            </button>
            {baum?.kette.map((k, i) => (
              <span key={k.pfad} className="flex min-w-0 items-center gap-1">
                <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-ps-dark-300" />
                {i === baum.kette.length - 1
                  ? <span className="break-words font-semibold text-ps-dark-900">{k.name}</span>
                  : <button onClick={() => waehlen(k.pfad)} className="break-words rounded px-1 py-0.5 text-left text-ps-dark-700 hover:bg-gray-100">{k.name}</button>}
              </span>
            ))}
          </nav>
          <div className="relative max-w-sm">
            <FunnelIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-ps-dark-400" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="In diesem Ordner filtern" aria-label="In diesem Ordner filtern"
              className="input-field py-1.5 pl-8 text-sm" />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {fehler && <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{fehler}</div>}
          {!baum && !fehler && <div className="space-y-2 p-4"><div className="shimmer h-3 w-1/3 rounded" /><div className="shimmer h-3 w-2/3 rounded" /></div>}
          {leer && (
            <div className="px-4 py-10 text-center text-sm text-ps-dark-500">
              {pfad ? 'Dieser Ordner ist leer — oder Sie haben darauf keinen Zugriff.'
                : 'Ihnen sind noch keine Ordner freigegeben. Bitte wenden Sie sich an Ihren Administrator.'}
            </div>
          )}
          {baum && !leer && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-ps-dark-400">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="hidden px-3 py-2 font-medium md:table-cell">Geändert</th>
                  <th className="hidden px-3 py-2 text-right font-medium sm:table-cell">Grösse</th>
                  <th className="px-3 py-2 font-medium">Stand</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ordner.map((o) => (
                  <tr key={o.pfad} className="cursor-pointer hover:bg-gray-50" onClick={() => waehlen(o.pfad)}>
                    <td className="px-4 py-2">
                      <button type="button" className="flex min-w-0 items-center gap-2 text-left font-medium text-ps-dark-900" onClick={(e) => { e.stopPropagation(); waehlen(o.pfad); }}>
                        <OrdnerIcon o={o} /><span className="min-w-0 break-words [overflow-wrap:anywhere]">{o.name}</span>
                      </button>
                    </td>
                    <td className="hidden px-3 py-2 text-ps-dark-500 md:table-cell">{datumCH(o.geaendert)}</td>
                    <td className="numerical hidden px-3 py-2 text-right text-ps-dark-500 sm:table-cell">{bytes(o.groesse)}</td>
                    <td className="px-3 py-2 text-xs text-ps-dark-500">{zahl(o.dateien)} {o.dateien === 1 ? 'Datei' : 'Dateien'}</td>
                  </tr>
                ))}
                {dateien.map((d) => {
                  const Icon = dateiIcon(d.endung);
                  const [label, badge] = STATUS[d.status] ?? [d.status, 'badge-neutral'];
                  return (
                    <tr key={d.id} className={`cursor-pointer ${d.id === datei ? 'bg-primary-50' : 'hover:bg-gray-50'}`} onClick={() => oeffnen(d)}>
                      <td className="px-4 py-2">
                        <button type="button" className="flex min-w-0 items-center gap-2 text-left text-ps-dark-800" onClick={(e) => { e.stopPropagation(); oeffnen(d); }}>
                          <Icon className="h-4 w-4 shrink-0 text-ps-dark-400" /><span className="min-w-0 break-words">{d.name}</span>
                        </button>
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-2 text-ps-dark-500 md:table-cell">{datumCH(d.geaendert)}</td>
                      <td className="numerical hidden whitespace-nowrap px-3 py-2 text-right text-ps-dark-500 sm:table-cell">{bytes(d.groesse)}</td>
                      <td className="px-3 py-2"><span className={`badge ${badge} whitespace-nowrap`}>{label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {baum && !leer && f && ordner.length === 0 && dateien.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-ps-dark-500">Nichts gefunden für «{filter}».</div>
          )}
          {baum && baum.weitere > 0 && (
            <div className="px-4 py-3 text-xs text-ps-dark-500">Angezeigt werden die ersten {zahl(baum.dateien.length)} Dateien — {zahl(baum.weitere)} weitere. Nutzen Sie „Fragen“, um gezielt zu suchen.</div>
          )}
        </div>
      </section>

      {datei && (
        <div className="fixed inset-0 z-50 bg-white lg:static lg:z-auto lg:w-[42%] lg:shrink-0 lg:bg-transparent">
          <DateiVorschau key={datei} id={datei} q="" um={null} onClose={schliessen} />
        </div>
      )}
    </div>
  );
}
